import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { selectClient, selectSessionAdvisor } from '@/model';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { pdfReportApi } from '@/api/waterlily';

export function useEmailPdfGuide() {
    const [isSending, setIsSending] = useState(false);
    const client = useSelector(selectClient);
    const sessionAdvisor = useSelector(selectSessionAdvisor);
    const isEdjPilot = useIsEdjPilotAdvisor();

    const sendOrOpen = useCallback(async (url: string) => {
        if (!isEdjPilot || !sessionAdvisor?.advisorId) {
            window.open(url, '_blank');
            return;
        }

        setIsSending(true);
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            await pdfReportApi.emailToAdvisor(sessionAdvisor.advisorId, {
                pdfBase64: base64,
                clientEmail: client?.clientEmail ?? '',
                clientName: `${client?.clientFirstName ?? ''} ${client?.clientLastName ?? ''}`.trim(),
                filename: 'How to Read Your Plan Summary.pdf',
                subject: 'How to Read Your Plan Summary – Waterlily Planning',
            });
            toast.success('Plan summary guide sent to your email');
        } catch {
            toast.error('Failed to send plan summary guide');
        } finally {
            setIsSending(false);
        }
    }, [isEdjPilot, sessionAdvisor, client]);

    return { sendOrOpen, isSending };
}
